/**
 * HistoryTab — activity feed for a lead.
 */
import { Loader2 } from "lucide-react";

const ACTION_LABELS = {
  search: "Search", save: "Saved to CRM", audit: "Audit",
  email: "Email generated", manual_add: "Manual add",
  status_change: "Status change", enrich: "Re-enrichment",
  whatsapp_sent: "WhatsApp sent", whatsapp_failed: "WhatsApp failed",
  whatsapp_opened: "WhatsApp opened", whatsapp_copied: "WhatsApp message copied",
  whatsapp_optout: "WhatsApp opt-out changed",
  address_update: "Address updated",
  webhook_fired: "Webhook fired", lead_update: "Lead updated",
  bulk_search: "Bulk search", lookalike: "AI Lookalike run",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function HistoryTab({ history, loadingHistory, t }) {
  return (
    <div data-testid="ldd-history-list">
      {loadingHistory ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-[#3B82F6]" />
        </div>
      ) : !history || history.items.length === 0 ? (
        <p className="text-[12px] text-[var(--al-text-muted)] text-center py-6 italic">{t("ldd.history_empty")}</p>
      ) : (
        <ul className="space-y-2.5">
          {history.items.map((it, idx) => (
            <li key={`${it.timestamp}-${idx}`} data-testid={`ldd-history-item-${idx}`}
              className="border border-[var(--al-border)] rounded-lg p-2.5 bg-[var(--al-bg)]">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[11.5px] font-semibold text-[var(--al-text)]">{ACTION_LABELS[it.action] || it.action}</span>
                <span className="text-[10.5px] text-[var(--al-text-muted)]">{fmtDate(it.timestamp)}</span>
              </div>
              {it.action === "status_change" && it.details && (
                <div className="text-[11px] text-[var(--al-text-muted)]">
                  {it.details.from} → <strong className="text-[var(--al-text)]">{it.details.to}</strong>
                  {it.details.notes ? ` · ${it.details.notes}` : ""}
                </div>
              )}
              {it.action === "enrich" && it.details && (
                <div className="text-[11px] text-[var(--al-text-muted)]">
                  {it.details.old_score ?? "—"} → <strong className="text-[var(--al-text)]">{it.details.new_score}</strong>
                  {" · "}{it.details.old_badge || "—"} → {it.details.new_badge}
                </div>
              )}
              {it.action === "whatsapp_sent" && it.details && (
                <div className="text-[11px] text-[var(--al-text-muted)] line-clamp-2">{(it.details.body || "").slice(0, 140)}</div>
              )}
              {it.action === "address_update" && it.details && (
                <div className="text-[11px] text-[var(--al-text-muted)] space-y-0.5">
                  {it.details.previous_address && (
                    <div className="line-through opacity-70">{it.details.previous_address}</div>
                  )}
                  <div className="text-[var(--al-text)] font-medium">{it.details.new_address}</div>
                  <div>
                    {it.details.source && <>Source: {it.details.source}</>}
                    {it.details.confidence ? ` · Confidence: ${it.details.confidence}%` : ""}
                    {it.details.updated_by ? ` · By: ${it.details.updated_by}` : ""}
                  </div>
                </div>
              )}
              {(it.action === "whatsapp_opened" || it.action === "whatsapp_copied") && it.details?.message_preview && (
                <div className="text-[11px] text-[var(--al-text-muted)] line-clamp-2">{it.details.message_preview}</div>
              )}
              {it.action === "lead_update" && it.details?.fields && (
                <div className="text-[11px] text-[var(--al-text-muted)]">{it.details.fields.join(", ")}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
